import type { MetadataRegistryEntry } from '@compass/client/types/metadata_registry';
import { RotateCcw } from 'lucide-react';
import React from 'react';

import { useMetadataAdvancedFilters } from '@/components/metadata-filters/MetadataAdvancedFilters';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchInput } from '@/components/ui/search-input';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import type { SupportedTimeZone } from '@/constants/date';
import { type QcScoreBand, SCORE_BAND_OPTIONS } from '@/pages/qc/utils';
import { dateToNaiveString, getTodayDateInTimezone, naiveStringToDate } from '@/utils/date';

import { QcMoreActionsMenu } from './QcMoreActionsMenu';

interface FilterOption {
  label: string;
  value: string;
}

export type QcSearchType = 'evaluation_id' | 'call_id';

interface QcListFiltersProps {
  activeSearchType: QcSearchType;
  onSearchTypeChange: (type: QcSearchType) => void;
  searchValues: Record<string, string>;
  onSearchValueChange: (type: string, value: string) => void;
  selectedProgramIds: string[];
  onProgramFilterChange: (value: string[]) => void;
  programOptions: FilterOption[];
  selectedAgentIds: string[];
  onAgentFilterChange: (value: string[]) => void;
  agentOptions: FilterOption[];
  selectedCallDirections: string[];
  onCallDirectionFilterChange: (value: string[]) => void;
  callDirectionOptions: FilterOption[];
  scoreBandFilter: QcScoreBand;
  onScoreBandFilterChange: (value: QcScoreBand) => void;
  dateFrom?: string;
  dateTo?: string;
  onDateRangeChange: (range: { from: string; to: string }) => void;
  timeZone: SupportedTimeZone;
  showResetFilters: boolean;
  onResetFilters: () => void;
  onlyIneligible: boolean;
  onOnlyIneligibleChange: (next: boolean) => void;
  onlyReviewed: boolean;
  onOnlyReviewedChange: (next: boolean) => void;
  // Metadata advanced filters (AQ-496).
  metadataDefinitions: MetadataRegistryEntry[];
  selectedMetadataValues: Record<string, string[]>;
  onMetadataFilterChange: (key: string, values: string[]) => void;
  organizationId?: string | null;
  metadataCallStartTimeAfter?: string;
  metadataCallStartTimeBefore?: string;
}

const QC_SEARCH_OPTIONS = [
  { key: 'evaluation_id', label: 'Evaluation ID' },
  { key: 'call_id', label: 'Call ID' },
];

const QC_SEARCH_PLACEHOLDER = 'Type to search';

export const QcListFilters: React.FC<QcListFiltersProps> = ({
  activeSearchType,
  onSearchTypeChange,
  searchValues,
  onSearchValueChange,
  selectedProgramIds,
  onProgramFilterChange,
  programOptions,
  selectedAgentIds,
  onAgentFilterChange,
  agentOptions,
  selectedCallDirections,
  onCallDirectionFilterChange,
  callDirectionOptions,
  scoreBandFilter,
  onScoreBandFilterChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
  timeZone,
  showResetFilters,
  onResetFilters,
  onlyIneligible,
  onOnlyIneligibleChange,
  onlyReviewed,
  onOnlyReviewedChange,
  metadataDefinitions,
  selectedMetadataValues,
  onMetadataFilterChange,
  organizationId,
  metadataCallStartTimeAfter,
  metadataCallStartTimeBefore,
}) => {
  const hasActiveMetadata = React.useMemo(
    () => Object.values(selectedMetadataValues).some(values => values.length > 0),
    [selectedMetadataValues],
  );

  const { toggle: metadataToggle, panel: metadataPanel } = useMetadataAdvancedFilters({
    definitions: metadataDefinitions,
    selectedValues: selectedMetadataValues,
    onChange: onMetadataFilterChange,
    organizationId,
    programIds: selectedProgramIds,
    callStartTimeAfter: metadataCallStartTimeAfter,
    callStartTimeBefore: metadataCallStartTimeBefore,
    defaultExpanded: hasActiveMetadata,
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          searchOptions={QC_SEARCH_OPTIONS}
          activeSearchType={activeSearchType}
          searchValues={searchValues}
          onSearchValueChange={onSearchValueChange}
          onSearchTypeChange={type => onSearchTypeChange(type as QcSearchType)}
          placeholder={() => QC_SEARCH_PLACEHOLDER}
          className="w-[360px]"
          searchTypeTriggerClassName="w-max min-w-max shrink-0 [&>span]:flex-none"
          minWidth="160px"
        />

        <SearchableMultiSelect
          options={programOptions}
          selected={selectedProgramIds}
          onChange={onProgramFilterChange}
          placeholder="Program"
          className="w-[175px] px-3"
          contentClassName="w-[18rem]"
          searchPlaceholder="Search programs..."
          emptyMessage="No programs found."
          maxCount={2}
        />

        <SearchableMultiSelect
          options={agentOptions}
          selected={selectedAgentIds}
          onChange={onAgentFilterChange}
          placeholder="Agent"
          className="w-[175px] px-3"
          contentClassName="w-[20rem]"
          searchPlaceholder="Search agents..."
          emptyMessage="No agents found."
          showSelectAll
        />

        <SearchableMultiSelect
          options={callDirectionOptions}
          selected={selectedCallDirections}
          onChange={onCallDirectionFilterChange}
          placeholder="Direction"
          className="w-38 px-3"
          contentClassName="w-auto min-w-[180px]"
          searchPlaceholder="Search directions..."
          emptyMessage="No directions found."
        />

        <Select
          value={scoreBandFilter}
          onValueChange={value => onScoreBandFilterChange(value as QcScoreBand)}
        >
          <SelectTrigger className="w-auto gap-2 [&>span]:flex-none">
            <SelectValue>
              {scoreBandFilter === 'all'
                ? 'Score'
                : (SCORE_BAND_OPTIONS.find(o => o.value === scoreBandFilter)?.label ?? 'Score')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {SCORE_BAND_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DateRangePicker
          initialDateFrom={naiveStringToDate(dateFrom || '') || getTodayDateInTimezone(timeZone)}
          initialDateTo={naiveStringToDate(dateTo || '') || getTodayDateInTimezone(timeZone)}
          timezone={timeZone}
          showCompare={false}
          onUpdate={({ range }) => {
            if (!range.from) {
              return;
            }

            onDateRangeChange({
              from: dateToNaiveString(range.from),
              to: dateToNaiveString(range.to || range.from),
            });
          }}
        />

        {metadataToggle}

        {showResetFilters ? (
          <SimpleTooltipV2 content="Reset filters" side="top">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10"
              onClick={onResetFilters}
              aria-label="Reset filters"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </SimpleTooltipV2>
        ) : null}

        <QcMoreActionsMenu
          onlyIneligible={onlyIneligible}
          onOnlyIneligibleChange={onOnlyIneligibleChange}
          onlyReviewed={onlyReviewed}
          onOnlyReviewedChange={onOnlyReviewedChange}
        />
      </div>

      {metadataPanel}
    </div>
  );
};
